import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge } from "@/components/brand/StatusBadge";
import { toast } from "sonner";
import { Loader2, Factory, Plus, ExternalLink, Pencil } from "lucide-react";
import { eur, QUOTE_PRODUCTS } from "@/lib/crm";

function statusTone(s, isKo) {
  if (isKo || s === "KO") return "danger";
  if (s === "Completata") return "success";
  if (s === "Sospesa") return "warning";
  if (s === "In lavorazione") return "info";
  return "pending";
}

export default function ProductionPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [offices, setOffices] = useState([]);
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [fFrom, setFrom] = useState("");
  const [fTo, setTo] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fProduct, setFProduct] = useState("");
  const [fOffice, setFOffice] = useState("");
  const [fOperator, setFOperator] = useState("");
  const [search, setSearch] = useState("");
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  useEffect(() => {
    api.get("/production/statuses").then(({ data }) => setStatuses(data)).catch(() => {});
    api.get("/offices").then(({ data }) => setOffices(data)).catch(() => {});
    if (isAdmin) api.get("/users").then(({ data }) => setOperators(data.filter((u) => u.active))).catch(() => {});
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = {};
      if (fFrom) params.date_from = fFrom;
      if (fTo) params.date_to = fTo;
      if (fStatus) params.status = fStatus;
      if (fProduct) params.product = fProduct;
      if (fOffice) params.office_id = fOffice;
      if (fOperator) params.operator_id = fOperator;
      if (search) params.search = search;
      const { data } = await api.get("/production", { params });
      setRows(data);
    } catch (e) { setError("Impossibile caricare la produzione."); } finally { setLoading(false); }
  }, [fFrom, fTo, fStatus, fProduct, fOffice, fOperator, search]);
  useEffect(() => { load(); }, [load]);

  const koSet = new Set(statuses.filter((s) => s.is_ko).map((s) => s.name));

  return (
    <div className="animate-fade-in-up space-y-5" data-testid="production-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="ds-page-title flex items-center gap-2"><Factory className="h-6 w-6 text-brand" /> Produzione</h1>
          <p className="mt-1 text-sm text-slate-500">Pratiche di vendita e avanzamento amministrativo.</p>
        </div>
        <Button onClick={() => { setEditRow(null); setDlgOpen(true); }} data-testid="new-production-btn" className="gap-1.5"><Plus className="h-4 w-4" /> Nuova pratica</Button>
      </div>

      <div className="ds-card flex flex-wrap items-end gap-3 p-4" data-testid="production-filters">
        <div className="space-y-1"><Label className="ds-label">Dal</Label><Input type="date" value={fFrom} onChange={(e) => setFrom(e.target.value)} data-testid="prod-from" /></div>
        <div className="space-y-1"><Label className="ds-label">Al</Label><Input type="date" value={fTo} onChange={(e) => setTo(e.target.value)} data-testid="prod-to" /></div>
        <Select value={fStatus || "all"} onValueChange={(v) => setFStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[160px]" data-testid="prod-status-filter"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti gli stati</SelectItem>{statuses.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={fProduct || "all"} onValueChange={(v) => setFProduct(v === "all" ? "" : v)}>
          <SelectTrigger className="w-[150px]" data-testid="prod-product-filter"><SelectValue placeholder="Prodotto" /></SelectTrigger>
          <SelectContent className="bg-white"><SelectItem value="all">Tutti i prodotti</SelectItem>{QUOTE_PRODUCTS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
        </Select>
        {isAdmin && (<>
          <Select value={fOffice || "all"} onValueChange={(v) => setFOffice(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[140px]" data-testid="prod-office-filter"><SelectValue placeholder="Sede" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Tutte</SelectItem>{offices.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fOperator || "all"} onValueChange={(v) => setFOperator(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[150px]" data-testid="prod-operator-filter"><SelectValue placeholder="Agente" /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="all">Tutti</SelectItem>{operators.map((o) => <SelectItem key={o.id} value={o.id}>{o.full_name}</SelectItem>)}</SelectContent>
          </Select>
        </>)}
        <Input placeholder="Cerca cliente…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-[180px]" data-testid="prod-search" />
      </div>

      {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        : error ? <div className="ds-card py-10 text-center text-sm text-danger" data-testid="production-error">{error}</div>
        : rows.length === 0 ? <div className="ds-card py-12 text-center text-sm text-slate-500" data-testid="production-empty">Nessuna pratica di produzione. Crea la prima da un preventivo accettato o un'opportunità vinta.</div>
        : (
          <div className="ds-card ds-table-scroll p-0" data-testid="production-table">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wider text-slate-500">
                <tr><th className="px-4 py-3">Pratica</th><th className="px-4 py-3">Cliente</th><th className="px-4 py-3">Prodotto</th><th className="px-4 py-3">Valore</th><th className="px-4 py-3">Data</th><th className="px-4 py-3">Agente</th><th className="px-4 py-3">Stato</th><th className="px-4 py-3">Origine</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((p) => (
                  <tr key={p.id} className="hover:bg-brand/5" data-testid={`production-row-${p.id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-slate-700">{p.production_number}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{p.customer_name || "—"}</td>
                    <td className="px-4 py-3">{p.product || "—"}</td>
                    <td className="px-4 py-3">{p.value != null ? eur(p.value) : "—"}</td>
                    <td className="px-4 py-3">{p.sale_date || "—"}</td>
                    <td className="px-4 py-3">{p.operator_name || "—"}</td>
                    <td className="px-4 py-3"><StatusBadge type={statusTone(p.status, koSet.has(p.status))} testid={`prod-status-${p.id}`}>{p.status}</StatusBadge></td>
                    <td className="px-4 py-3 text-xs">{p.is_manual ? <span className="rounded bg-slate-100 px-1.5 py-0.5 text-slate-600">Manuale</span> : <span className="rounded bg-brand/10 px-1.5 py-0.5 text-brand">{p.source_type === "quote" ? "Preventivo" : "Opportunità"}</span>}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" title="Modifica" onClick={() => { setEditRow(p); setDlgOpen(true); }} data-testid={`prod-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" title="Apri cliente" onClick={() => navigate(`/clienti/${p.customer_id}`)} data-testid={`prod-open-${p.id}`}><ExternalLink className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

      {dlgOpen && <ProductionDialog open={dlgOpen} onOpenChange={setDlgOpen} row={editRow} statuses={statuses} isAdmin={isAdmin} onSaved={load} />}
    </div>
  );
}

function ProductionDialog({ open, onOpenChange, row, statuses, isAdmin, onSaved }) {
  const editing = !!row;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [eligible, setEligible] = useState({ quotes: [], opportunities: [] });
  const [mode, setMode] = useState("quote"); // quote | opportunity | manual
  const [selSource, setSelSource] = useState("");
  const [status, setStatus] = useState(row?.status || "Da verificare");
  const [value, setValue] = useState(row?.value ?? "");
  const [notes, setNotes] = useState(row?.notes || "");
  const srcSelected = !editing && !!selSource;

  useEffect(() => {
    if (!editing) api.get("/production/eligible").then(({ data }) => setEligible(data)).catch(() => {});
  }, [editing]);

  useEffect(() => {
    if (editing) return;
    const list = mode === "quote" ? eligible.quotes : eligible.opportunities;
    const found = list.find((x) => x.id === selSource);
    if (found) setValue(found.value != null ? String(found.value) : "");
  }, [selSource, mode, eligible, editing]);

  const save = async () => {
    setError(""); setSaving(true);
    try {
      if (editing) {
        await api.put(`/production/${row.id}`, { status, value: value === "" ? null : Number(value), notes });
        toast.success("Pratica aggiornata");
      } else {
        const payload = { source_type: mode, status, notes, value: value === "" ? null : Number(value) };
        if (mode === "quote") payload.quote_id = selSource;
        else payload.opportunity_id = selSource;
        if (!selSource) { setError("Seleziona una sorgente."); setSaving(false); return; }
        await api.post("/production", payload);
        toast.success("Pratica creata");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto bg-white sm:max-w-lg" data-testid="production-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display"><Factory className="h-5 w-5 text-brand" /> {editing ? `Pratica ${row.production_number}` : "Nuova pratica di produzione"}</DialogTitle>
          <DialogDescription>{editing ? "Aggiorna stato, valore e note amministrative." : "Genera la pratica da un preventivo accettato o un'opportunità vinta."}</DialogDescription>
        </DialogHeader>
        {error && <div className="rounded-lg border border-danger/25 bg-danger/10 px-3 py-2 text-sm text-danger" data-testid="production-dialog-error">{error}</div>}

        {!editing && (
          <div className="space-y-3">
            <div className="flex gap-2">
              <Button variant={mode === "quote" ? "default" : "outline"} size="sm" onClick={() => { setMode("quote"); setSelSource(""); }} data-testid="prod-mode-quote">Da preventivo</Button>
              <Button variant={mode === "opportunity" ? "default" : "outline"} size="sm" onClick={() => { setMode("opportunity"); setSelSource(""); }} data-testid="prod-mode-opp">Da opportunità</Button>
            </div>
            {mode === "quote" ? (
              <div className="space-y-1.5"><Label>Preventivo accettato *</Label>
                <Select value={selSource} onValueChange={setSelSource}>
                  <SelectTrigger data-testid="prod-source-quote"><SelectValue placeholder={eligible.quotes.length ? "Seleziona preventivo" : "Nessun preventivo accettato disponibile"} /></SelectTrigger>
                  <SelectContent className="bg-white">{eligible.quotes.map((q) => <SelectItem key={q.id} value={q.id}>{q.quote_number} · {q.customer_name} · {q.product || "—"} · {q.value != null ? eur(q.value) : "—"}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            ) : (
              <div className="space-y-1.5"><Label>Opportunità vinta *</Label>
                <Select value={selSource} onValueChange={setSelSource}>
                  <SelectTrigger data-testid="prod-source-opp"><SelectValue placeholder={eligible.opportunities.length ? "Seleziona opportunità" : "Nessuna opportunità vinta disponibile"} /></SelectTrigger>
                  <SelectContent className="bg-white">{eligible.opportunities.map((o) => <SelectItem key={o.id} value={o.id}>{o.customer_name} · {o.product} · {o.value != null ? eur(o.value) : "—"}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label>Stato</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="prod-status-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{statuses.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Valore (€){srcSelected ? " · da sorgente" : ""}</Label>
            <Input type="number" step="0.01" value={value} onChange={(e) => setValue(e.target.value)} data-testid="prod-value-input" placeholder={srcSelected ? "Recuperato dalla sorgente" : "Valore vendita"} disabled={srcSelected} />
            {srcSelected && <p className="text-[11px] text-slate-400" data-testid="prod-value-source-hint">Valore derivato dal preventivo/opportunità selezionato.</p>}
          </div>
        </div>
        <div className="space-y-1.5"><Label>Note amministrative</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="prod-notes" /></div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button onClick={save} disabled={saving} data-testid="prod-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
